(function () {
  "use strict";

  document.querySelectorAll("[data-video-reels]").forEach(initReelsWidget);

  const rootOverlays = new WeakMap();

  function optimizedThumbnail(url, width) {
    if (!url) return url;
    try {
      const u = new URL(url, window.location.href);
      if (!/(^|\.)cdn\.shopify\.com$/.test(u.hostname)) return url;
      u.searchParams.set("width", String(width));
      return u.toString();
    } catch {
      return url;
    }
  }

  async function initReelsWidget(root) {
    const apiBase = root.dataset.apiBase;
    const placement = root.dataset.placement;
    const store = root.dataset.store || "uk";
    if (!apiBase || !placement) return;

    let videos = [];
    let style = null;
    try {
      const res = await fetch(`${apiBase}/api/public/${encodeURIComponent(placement)}?store=${encodeURIComponent(store)}`);
      const data = await res.json();
      videos = data.videos || [];
      style = data.style || null;
    } catch (e) {
      console.warn("Video reels: failed to load", e);
      return;
    }
    if (!videos.length) return;

    let previewOverride = null;
    try {
      previewOverride = JSON.parse(root.dataset.previewStyle || "null");
    } catch (e) {
      previewOverride = null;
    }

    const styleType = previewOverride?.style || style?.type || root.dataset.style || "stories";
    const resolvedCircleSize = previewOverride?.circleSize || style?.circleSize || Number(root.dataset.circleSize) || 100;
    root.style.setProperty("--lr-accent", previewOverride?.accentColor || style?.accentColor || root.dataset.accent || "#cea761");
    root.style.setProperty("--lr-circle-size", resolvedCircleSize + "px");
    root.style.setProperty(
      "--lr-card-width",
      (previewOverride?.cardWidth || style?.cardWidth || root.dataset.cardWidth || "140") + "px"
    );
    root.dataset.resolvedFloatingPosition =
      previewOverride?.floatingPosition || style?.floatingPosition || root.dataset.floatingPosition || "bottom-right";

    if (styleType === "carousel") {
      initCarousel(root, videos, apiBase, placement, store);
    } else if (styleType === "floating") {
      root.style.margin = "0";
      initFloating(root, videos, apiBase, placement, store);
    } else if (styleType === "hero") {
      initHero(root, videos, apiBase, placement, store);
    } else if (styleType === "grid") {
      initGrid(root, videos, apiBase, placement, store);
    } else if (styleType === "filmstrip") {
      initFilmstrip(root, videos, apiBase, placement, resolvedCircleSize, store);
    } else if (styleType === "spotlight") {
      initSpotlight(root, videos, apiBase, placement, store);
    } else {
      initStories(root, videos, apiBase, placement, resolvedCircleSize, store);
    }
  }

  function attachFadeOnPlay(items, iconSelector) {
    items.forEach((el) => {
      const video = el.querySelector("video");
      const icon = iconSelector ? el.querySelector(iconSelector) : null;
      if (!video) return;
      video.addEventListener("playing", () => {
        video.style.opacity = "1";
        if (icon) icon.style.opacity = "0";
      });
      video.addEventListener("pause", () => {
        video.style.opacity = "0";
        if (icon) icon.style.opacity = "1";
      });
      video.addEventListener("emptied", () => {
        video.style.opacity = "0";
        if (icon) icon.style.opacity = "1";
      });
    });
  }

  function attachAutoplayRow(items, apiBase, videos, placement, store) {
    const preloadIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const video = entry.target.querySelector("video");
          if (!video || !video.dataset.src) return;
          video.src = video.dataset.src;
          delete video.dataset.src;
          sendEvent(apiBase, videos[Number(entry.target.dataset.index)].id, "impression", placement, store);
        });
      },
      { rootMargin: "600px 0px", threshold: 0.01 }
    );
    const playIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector("video");
          if (!video) return;
          if (entry.isIntersecting) video.play().catch(() => {});
          else video.pause();
        });
      },
      { threshold: 0.5 }
    );
    items.forEach((el) => {
      preloadIO.observe(el);
      playIO.observe(el);
    });
  }

  function initStories(root, videos, apiBase, placement, desktopCircleSize, store) {
    const track = root.querySelector("[data-video-reels-track]");
    if (!track) return;

    const circleBtnStyle =
      "all:unset;box-sizing:border-box;flex:0 0 auto;width:80px;cursor:pointer;text-align:center;display:inline-block;";
    const circleMediaStyle =
      "display:block;box-sizing:border-box;width:80px;height:80px;padding:3px;border-radius:50%;border:3px solid var(--lr-accent, #cea761);background:#fff;position:relative;";
    const circleMediaInnerStyle = "display:block;width:100%;height:100%;border-radius:50%;overflow:hidden;background:#eee;position:relative;";
    const circleImgStyle = "display:block;width:100%;height:100%;object-fit:cover;background:#eee;";
    const circleVideoStyle =
      "display:block;width:100%;height:100%;object-fit:cover;position:absolute;inset:0;opacity:0;transition:opacity 0.2s ease;";
    const circlePlayIconStyle =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,0.5);pointer-events:none;transition:opacity 0.15s ease;";

    track.innerHTML = videos
      .map(
        (v, i) => `
      <button class="video-reels__circle" style="${circleBtnStyle}" data-index="${i}" aria-label="Play video: ${escapeHtml(v.title)}">
        <span class="video-reels__circle-media" style="${circleMediaStyle}">
          <span class="video-reels__circle-media-inner" style="${circleMediaInnerStyle}">
            ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 220)}" alt="" loading="lazy" style="${circleImgStyle}">` : ""}
            <video
              style="${circleVideoStyle}"
              data-src="${v.videoUrl}"
              muted
              loop
              playsinline
              preload="none"
            ></video>
            <span class="video-reels__circle-play-icon" style="${circlePlayIconStyle}">${ICON_PLAY_CIRCLE}</span>
          </span>
        </span>
      </button>
    `
      )
      .join("");

    const circles = track.querySelectorAll(".video-reels__circle");
    attachFadeOnPlay(circles, ".video-reels__circle-play-icon");

    const CIRCLE_GAP = 14;
    const DESKTOP_CIRCLE = desktopCircleSize || 100;
    function fitDesktopTrack() {
      if (!window.matchMedia("(min-width: 750px)").matches) {
        track.style.width = "";
        return;
      }
      const available = root.clientWidth;
      if (!available) return;
      let count = Math.floor((available + CIRCLE_GAP) / (DESKTOP_CIRCLE + CIRCLE_GAP));
      count = Math.max(1, Math.min(count, videos.length));
      const trackWidth = count * DESKTOP_CIRCLE + count * CIRCLE_GAP;
      track.style.width = trackWidth + "px";
    }
    fitDesktopTrack();
    if (window.ResizeObserver) {
      new ResizeObserver(fitDesktopTrack).observe(root);
    } else {
      window.addEventListener("resize", fitDesktopTrack);
    }

    attachAutoplayRow(circles, apiBase, videos, placement, store);

    track.addEventListener("click", (e) => {
      const btn = e.target.closest(".video-reels__circle");
      if (!btn) return;
      openOverlay(root, videos, Number(btn.dataset.index), apiBase, placement, store);
    });
  }

  function initCarousel(root, videos, apiBase, placement, store) {
    const track = root.querySelector("[data-video-reels-track]");
    if (!track) return;

    const cardBtnStyle = "all:unset;box-sizing:border-box;flex:0 0 auto;width:var(--lr-card-width, 140px);cursor:pointer;";
    const cardMediaStyle =
      "display:block;position:relative;width:100%;aspect-ratio:9/16;border-radius:12px;overflow:hidden;border:2px solid var(--lr-accent, #cea761);background:#eee;";
    const cardImgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;background:#eee;";
    const cardVideoStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s ease;";
    const cardCaptionStyle =
      "position:absolute;left:8px;right:8px;bottom:8px;color:#fff;font-size:12px;font-weight:600;line-height:1.3;text-shadow:0 1px 3px rgba(0,0,0,0.6);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;pointer-events:none;";
    const cardPlayIconStyle =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,0.5);pointer-events:none;transition:opacity 0.15s ease;";

    track.innerHTML = videos
      .map(
        (v, i) => `
      <button class="video-reels__carousel-card" style="${cardBtnStyle}" data-index="${i}" aria-label="Play video: ${escapeHtml(v.title)}">
        <span class="video-reels__carousel-media" style="${cardMediaStyle}">
          ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 400)}" alt="" loading="lazy" style="${cardImgStyle}">` : ""}
          <video
            style="${cardVideoStyle}"
            data-src="${v.videoUrl}"
            muted
            loop
            playsinline
            preload="none"
          ></video>
          <span class="video-reels__carousel-play-icon" style="${cardPlayIconStyle}">${ICON_PLAY_CIRCLE}</span>
          <span style="${cardCaptionStyle}">${escapeHtml(v.title)}</span>
        </span>
      </button>
    `
      )
      .join("");

    const cards = track.querySelectorAll(".video-reels__carousel-card");
    attachFadeOnPlay(cards, ".video-reels__carousel-play-icon");
    attachAutoplayRow(cards, apiBase, videos, placement, store);

    track.addEventListener("click", (e) => {
      const btn = e.target.closest(".video-reels__carousel-card");
      if (!btn) return;
      openOverlay(root, videos, Number(btn.dataset.index), apiBase, placement, store);
    });
  }

  function initFloating(root, videos, apiBase, placement, store) {
    const position = root.dataset.resolvedFloatingPosition || "bottom-right";
    const v = videos[0];

    const wrapStyle = "all:unset;box-sizing:border-box;position:fixed;z-index:2147483000;width:150px;";
    const cardStyle =
      "all:unset;box-sizing:border-box;display:block;position:relative;width:100%;aspect-ratio:9/16;border-radius:14px;overflow:hidden;cursor:pointer;border:2px solid var(--lr-accent, #cea761);box-shadow:0 8px 24px rgba(0,0,0,0.35);background:#000;";
    const imgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;";
    const videoStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s ease;";
    const captionStyle =
      "position:absolute;left:10px;right:10px;bottom:10px;color:#fff;font-size:13px;font-weight:600;line-height:1.3;text-shadow:0 1px 3px rgba(0,0,0,0.6);pointer-events:none;";
    const closeStyle =
      "all:unset;box-sizing:border-box;position:absolute;top:-10px;right:-10px;z-index:1;width:26px;height:26px;border-radius:50%;background:rgba(0,0,0,0.75);color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;";

    const wrap = document.createElement("div");
    wrap.className = `video-reels__floating video-reels__floating--${position}`;
    wrap.style.cssText = wrapStyle + positionStyleFor(position);
    wrap.innerHTML = `
      <button class="video-reels__floating-close" style="${closeStyle}" aria-label="Close">${ICON_CLOSE}</button>
      <button class="video-reels__floating-card" style="${cardStyle}" aria-label="Play video: ${escapeHtml(v.title)}">
        ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 400)}" alt="" style="${imgStyle}">` : ""}
        <video style="${videoStyle}" muted loop playsinline autoplay src="${v.videoUrl}"></video>
        <span style="${captionStyle}">${escapeHtml(v.title)}</span>
      </button>
    `;
    document.body.appendChild(wrap);

    const video = wrap.querySelector("video");
    video.addEventListener("playing", () => {
      video.style.opacity = "1";
    });
    video.play().catch(() => {});
    sendEvent(apiBase, v.id, "impression", placement, store);

    wrap.querySelector(".video-reels__floating-card").addEventListener("click", () => {
      openOverlay(root, videos, 0, apiBase, placement, store);
    });
    wrap.querySelector(".video-reels__floating-close").addEventListener("click", () => {
      video.pause();
      wrap.remove();
    });
  }

  function deriveHqSource(url) {
    if (!url) return null;
    const m = /^(https:\/\/cdn\.shopify\.com\/videos\/c\/)vp\/([a-z0-9]+)\/\2\.[^/?#]+\.mp4(?:[?#].*)?$/i.exec(url);
    return m ? `${m[1]}o/v/${m[2]}.mp4` : null;
  }

  function positionStyleFor(position) {
    switch (position) {
      case "bottom-left":
        return "bottom:16px;left:16px;";
      case "top-right":
        return "top:16px;right:16px;";
      case "top-left":
        return "top:16px;left:16px;";
      default:
        return "bottom:16px;right:16px;";
    }
  }

  function initHero(root, videos, apiBase, placement, store) {
    const track = root.querySelector("[data-video-reels-track]");
    if (!track) return;
    track.style.cssText = "display:block;overflow:visible;";

    const v = videos[0];
    const cardStyle =
      "all:unset;box-sizing:border-box;display:block;position:relative;width:100%;aspect-ratio:16/9;border-radius:10px;overflow:hidden;cursor:pointer;border:2px solid var(--lr-accent, #cea761);background:#000;";
    const imgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;";
    const videoStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s ease;";
    const playIconStyle =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 1px 4px rgba(0,0,0,0.6);pointer-events:none;transition:opacity 0.15s ease;";
    const captionStyle =
      "position:absolute;left:14px;right:14px;bottom:12px;color:#fff;font-size:14px;font-weight:600;line-height:1.3;text-shadow:0 1px 3px rgba(0,0,0,0.6);pointer-events:none;";

    track.innerHTML = `
      <button class="video-reels__hero-card" style="${cardStyle}" aria-label="Play video: ${escapeHtml(v.title)}">
        ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 900)}" alt="" style="${imgStyle}">` : ""}
        <video style="${videoStyle}" data-src="${v.videoUrl}" muted loop playsinline preload="none"></video>
        <span class="video-reels__hero-play-icon" style="${playIconStyle}">${ICON_PLAY_CIRCLE_LG}</span>
        <span style="${captionStyle}">${escapeHtml(v.title)}</span>
      </button>
    `;

    const card = track.querySelector(".video-reels__hero-card");
    attachFadeOnPlay([card], ".video-reels__hero-play-icon");
    attachAutoplayRow([card], apiBase, videos, placement, store);
    card.addEventListener("click", () => openOverlay(root, videos, 0, apiBase, placement, store));
  }

  function initGrid(root, videos, apiBase, placement, store) {
    const track = root.querySelector("[data-video-reels-track]");
    if (!track) return;
    track.style.cssText = "display:grid;grid-template-columns:repeat(auto-fill, minmax(var(--lr-card-width, 140px), 1fr));gap:12px;overflow:visible;";

    const cardStyle =
      "all:unset;box-sizing:border-box;display:block;cursor:pointer;width:100%;";
    const mediaStyle =
      "display:block;position:relative;width:100%;aspect-ratio:9/16;border-radius:10px;overflow:hidden;border:2px solid var(--lr-accent, #cea761);background:#eee;";
    const imgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;background:#eee;";
    const videoStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s ease;";
    const playIconStyle =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,0.5);pointer-events:none;transition:opacity 0.15s ease;";
    const captionStyle =
      "position:absolute;left:8px;right:8px;bottom:8px;color:#fff;font-size:12px;font-weight:600;line-height:1.3;text-shadow:0 1px 3px rgba(0,0,0,0.6);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;pointer-events:none;";

    track.innerHTML = videos
      .map(
        (v, i) => `
      <button class="video-reels__grid-card" style="${cardStyle}" data-index="${i}" aria-label="Play video: ${escapeHtml(v.title)}">
        <span class="video-reels__grid-media" style="${mediaStyle}">
          ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 400)}" alt="" loading="lazy" style="${imgStyle}">` : ""}
          <video style="${videoStyle}" data-src="${v.videoUrl}" muted loop playsinline preload="none"></video>
          <span class="video-reels__grid-play-icon" style="${playIconStyle}">${ICON_PLAY_CIRCLE}</span>
          <span style="${captionStyle}">${escapeHtml(v.title)}</span>
        </span>
      </button>
    `
      )
      .join("");

    const cards = track.querySelectorAll(".video-reels__grid-card");
    attachFadeOnPlay(cards, ".video-reels__grid-play-icon");
    attachAutoplayRow(cards, apiBase, videos, placement, store);
    track.addEventListener("click", (e) => {
      const btn = e.target.closest(".video-reels__grid-card");
      if (!btn) return;
      openOverlay(root, videos, Number(btn.dataset.index), apiBase, placement, store);
    });
  }

  function initFilmstrip(root, videos, apiBase, placement, thumbSize, store) {
    const track = root.querySelector("[data-video-reels-track]");
    if (!track) return;

    const size = thumbSize || 64;
    const btnStyle = `all:unset;box-sizing:border-box;flex:0 0 auto;width:${size}px;cursor:pointer;`;
    const mediaStyle = `display:block;position:relative;width:${size}px;height:${size}px;border-radius:6px;overflow:hidden;border:1.5px solid var(--lr-accent, #cea761);background:#eee;`;
    const imgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;background:#eee;";
    const videoStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s ease;";
    const playIconStyle =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,0.5);pointer-events:none;transition:opacity 0.15s ease;";

    track.innerHTML = videos
      .map(
        (v, i) => `
      <button class="video-reels__filmstrip-item" style="${btnStyle}" data-index="${i}" aria-label="Play video: ${escapeHtml(v.title)}">
        <span class="video-reels__filmstrip-media" style="${mediaStyle}">
          ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 160)}" alt="" loading="lazy" style="${imgStyle}">` : ""}
          <video style="${videoStyle}" data-src="${v.videoUrl}" muted loop playsinline preload="none"></video>
          <span class="video-reels__filmstrip-play-icon" style="${playIconStyle}">${ICON_PLAY_CIRCLE_SM}</span>
        </span>
      </button>
    `
      )
      .join("");

    const items = track.querySelectorAll(".video-reels__filmstrip-item");
    attachFadeOnPlay(items, ".video-reels__filmstrip-play-icon");
    attachAutoplayRow(items, apiBase, videos, placement, store);
    track.addEventListener("click", (e) => {
      const btn = e.target.closest(".video-reels__filmstrip-item");
      if (!btn) return;
      openOverlay(root, videos, Number(btn.dataset.index), apiBase, placement, store);
    });
  }

  function initSpotlight(root, videos, apiBase, placement, store) {
    const track = root.querySelector("[data-video-reels-track]");
    if (!track) return;
    track.style.cssText = "display:block;overflow:visible;max-width:280px;";

    const v = videos[0];
    const cardStyle =
      "all:unset;box-sizing:border-box;display:block;position:relative;width:100%;aspect-ratio:9/16;border-radius:14px;overflow:hidden;cursor:pointer;border:2px solid var(--lr-accent, #cea761);box-shadow:0 6px 18px rgba(0,0,0,0.18);background:#000;";
    const imgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;";
    const videoStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.2s ease;";
    const playIconStyle =
      "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;text-shadow:0 1px 4px rgba(0,0,0,0.6);pointer-events:none;transition:opacity 0.15s ease;";
    const captionStyle =
      "position:absolute;left:14px;right:14px;bottom:14px;color:#fff;font-size:14px;font-weight:600;line-height:1.3;text-shadow:0 1px 3px rgba(0,0,0,0.6);pointer-events:none;";
    const ctaStyle =
      "position:absolute;top:12px;left:12px;background:rgba(0,0,0,0.55);color:#fff;font-size:11px;font-weight:700;letter-spacing:0.03em;text-transform:uppercase;padding:4px 9px;border-radius:99px;pointer-events:none;";

    track.innerHTML = `
      <button class="video-reels__spotlight-card" style="${cardStyle}" aria-label="Play video: ${escapeHtml(v.title)}">
        ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 600)}" alt="" style="${imgStyle}">` : ""}
        <video style="${videoStyle}" data-src="${v.videoUrl}" muted loop playsinline preload="none"></video>
        <span style="${ctaStyle}">Watch</span>
        <span class="video-reels__spotlight-play-icon" style="${playIconStyle}">${ICON_PLAY_CIRCLE_LG}</span>
        <span style="${captionStyle}">${escapeHtml(v.title)}</span>
      </button>
    `;

    const card = track.querySelector(".video-reels__spotlight-card");
    attachFadeOnPlay([card], ".video-reels__spotlight-play-icon");
    attachAutoplayRow([card], apiBase, videos, placement, store);
    card.addEventListener("click", () => openOverlay(root, videos, 0, apiBase, placement, store));
  }

  const overlayStyle =
    "all:unset;box-sizing:border-box;position:fixed;inset:0;z-index:2147483000;background:rgba(0,0,0,0.92);align-items:center;justify-content:center;display:none;";
  const overlayOpenStyle = overlayStyle.replace("display:none", "display:flex");
  const closeBtnStyle =
    "all:unset;box-sizing:border-box;width:44px;height:44px;border-radius:50%;background:rgba(255,255,255,0.15);color:#fff;display:flex;align-items:center;justify-content:center;font-size:20px;line-height:1;cursor:pointer;";
  const navBtnStyle =
    "all:unset;box-sizing:border-box;width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,0.15);color:#fff;display:flex;align-items:center;justify-content:center;font-size:20px;line-height:1;cursor:pointer;";
  const stageStyle = "box-sizing:border-box;position:relative;width:min(90vw, 420px);height:min(calc(90vw * 16 / 9), 88vh);max-height:88vh;";
  const viewportStyle =
    "box-sizing:border-box;overflow-y:auto;overflow-x:hidden;background:#000;scroll-snap-type:y mandatory;scrollbar-width:none;-webkit-overflow-scrolling:touch;";
  const slideStyle =
    "box-sizing:border-box;position:relative;width:100%;height:100%;scroll-snap-align:start;scroll-snap-stop:always;flex-shrink:0;";
  const slideImgStyle = "position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;";
  const videoStyle2 =
    "position:absolute;inset:0;display:block;width:100%;height:100%;object-fit:contain;background:transparent;";
  const muteBtnStyle =
    "all:unset;box-sizing:border-box;position:absolute;top:56px;right:12px;z-index:2;width:36px;height:36px;border-radius:50%;background:rgba(0,0,0,0.5);color:#fff;display:flex;align-items:center;justify-content:center;font-size:16px;cursor:pointer;";
  const pauseBtnStyle =
    "all:unset;box-sizing:border-box;position:absolute;top:12px;right:12px;z-index:2;width:36px;height:36px;border-radius:50%;background:rgba(0,0,0,0.5);color:#fff;display:flex;align-items:center;justify-content:center;font-size:16px;cursor:pointer;";
  const progressTrackStyle =
    "box-sizing:border-box;position:absolute;top:0;left:0;right:0;z-index:2;height:3px;background:rgba(255,255,255,0.3);";
  const progressFillStyle = "display:block;height:100%;width:0%;background:#fff;transition:width 0.1s linear;";

  const ICON_CLOSE =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  const ICON_CHEVRON_LEFT =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>';
  const ICON_CHEVRON_RIGHT =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>';
  const ICON_PLAY = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
  const ICON_PAUSE =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>';
  const ICON_MUTE =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9v6h4l5 5V4L7 9H3z" fill="currentColor" stroke="none"/><path d="M16 9l6 6M22 9l-6 6"/></svg>';
  const ICON_UNMUTE =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9v6h4l5 5V4L7 9H3z" fill="currentColor" stroke="none"/><path d="M16 8a5 5 0 010 8M19 5a9 9 0 010 14"/></svg>';
  const ICON_PLAY_CIRCLE =
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M10 8.5v7l6-3.5z" fill="currentColor" stroke="none"/></svg>';
  const ICON_PLAY_CIRCLE_SM =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M10 8.5v7l6-3.5z" fill="currentColor" stroke="none"/></svg>';
  const ICON_PLAY_CIRCLE_LG =
    '<svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="12" cy="12" r="10"/><path d="M10 8.5v7l6-3.5z" fill="currentColor" stroke="none"/></svg>';

  function openOverlay(root, videos, startIndex, apiBase, placement, store) {
    let state = rootOverlays.get(root);

    if (!state) {
      const overlay = document.createElement("div");
      overlay.className = "video-reels__overlay";
      overlay.style.cssText = overlayStyle;
      overlay.innerHTML = `
        <div class="video-reels__stage" style="${stageStyle}">
          <button class="video-reels__overlay-close" style="${closeBtnStyle}" aria-label="Close">${ICON_CLOSE}</button>
          <button class="video-reels__overlay-prev" style="${navBtnStyle}" aria-label="Previous video">${ICON_CHEVRON_LEFT}</button>
          <button class="video-reels__overlay-next" style="${navBtnStyle}" aria-label="Next video">${ICON_CHEVRON_RIGHT}</button>
          <div class="video-reels__card" style="${viewportStyle}"></div>
        </div>
      `;
      document.body.appendChild(overlay);

      const viewport = overlay.querySelector(".video-reels__card");
      const muteBtn = overlay.querySelector(".video-reels__mute");
      const prevBtn = overlay.querySelector(".video-reels__overlay-prev");
      const nextBtn = overlay.querySelector(".video-reels__overlay-next");

      state = {
        overlay,
        viewport,
        prevBtn,
        nextBtn,
        videos,
        apiBase,
        placement,
        store,
        index: 0,
        openTrackedIndex: -1,
        muted: true,
        slides: [],
        suppressScrollEvents: false,
      };
      rootOverlays.set(root, state);

      function buildSlides() {
        viewport.innerHTML = state.videos
          .map(
            (v, i) => `
          <div class="video-reels__slide" data-index="${i}" style="${slideStyle}">
            <div class="video-reels__progress-track" style="${progressTrackStyle}">
              <div class="video-reels__progress-fill" style="${progressFillStyle}"></div>
            </div>
            ${v.thumbnailUrl ? `<img src="${optimizedThumbnail(v.thumbnailUrl, 800)}" alt="" style="${slideImgStyle}">` : ""}
            <video style="${videoStyle2}" playsinline loop data-src="${deriveHqSource(v.videoUrl) || v.videoUrl}" data-fallback-src="${v.videoUrl}"></video>
            <button class="video-reels__pause" style="${pauseBtnStyle}" aria-label="Pause">${ICON_PAUSE}</button>
            <button class="video-reels__mute" style="${muteBtnStyle}" aria-label="Unmute">${ICON_MUTE}</button>
          </div>
        `
          )
          .join("");

        state.slides = Array.from(viewport.querySelectorAll(".video-reels__slide")).map((el) => {
          const video = el.querySelector("video");
          const pauseBtn = el.querySelector(".video-reels__pause");
          const muteBtn = el.querySelector(".video-reels__mute");
          const progressFill = el.querySelector(".video-reels__progress-fill");

          video.addEventListener("timeupdate", () => {
            if (!video.duration) return;
            progressFill.style.width = `${(video.currentTime / video.duration) * 100}%`;
          });
          function togglePlay() {
            if (video.paused) video.play().catch(() => {});
            else video.pause();
          }
          video.addEventListener("click", togglePlay);
          pauseBtn.addEventListener("click", togglePlay);
          video.addEventListener("play", () => {
            pauseBtn.innerHTML = ICON_PAUSE;
            pauseBtn.setAttribute("aria-label", "Pause");
          });
          video.addEventListener("playing", () => {
            video.style.background = "#000";
          });
          video.addEventListener("pause", () => {
            pauseBtn.innerHTML = ICON_PLAY;
            pauseBtn.setAttribute("aria-label", "Play");
          });
          video.addEventListener("emptied", () => {
            video.style.background = "transparent";
          });
          video.addEventListener("error", () => {
            const fallback = video.dataset.fallbackSrc;
            if (!fallback || video.src === fallback) return;
            video.dataset.fallbackSrc = "";
            video.src = fallback;
            video.load();
            video.play().catch(() => {});
          });
          video.addEventListener("ended", () => {
            sendEvent(state.apiBase, state.videos[Number(el.dataset.index)].id, "watch_complete", state.placement, state.store);
          });
          muteBtn.addEventListener("click", () => {
            state.muted = !state.muted;
            state.slides.forEach((s) => (s.video.muted = state.muted));
            state.slides.forEach((s) => {
              s.muteBtn.innerHTML = state.muted ? ICON_MUTE : ICON_UNMUTE;
              s.muteBtn.setAttribute("aria-label", state.muted ? "Unmute" : "Mute");
            });
          });

          return { el, video, pauseBtn, muteBtn, progressFill };
        });

        state.slidePreloadObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const idx = Number(entry.target.dataset.index);
              const slide = state.slides[idx];
              if (!slide || !slide.video.dataset.src) return;
              slide.video.src = slide.video.dataset.src;
              delete slide.video.dataset.src;
            });
          },
          { root: viewport, rootMargin: "100% 100%", threshold: 0.01 }
        );

        state.slideObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const idx = Number(entry.target.dataset.index);
              const slide = state.slides[idx];
              if (!slide) return;
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                if (state.openTrackedIndex !== idx) {
                  state.openTrackedIndex = idx;
                  sendEvent(state.apiBase, state.videos[idx].id, "open", state.placement, state.store);
                }
                if (state.index !== idx) {
                  state.index = idx;
                  updateNavVisibility();
                }
                if (slide.video.dataset.src) {
                  slide.video.src = slide.video.dataset.src;
                  delete slide.video.dataset.src;
                }
                slide.video.muted = state.muted;
                slide.video.play().catch(() => {});
              } else {
                slide.video.pause();
              }
            });
          },
          { root: viewport, threshold: [0, 0.6, 1] }
        );
        state.slides.forEach((s) => {
          state.slidePreloadObserver.observe(s.el);
          state.slideObserver.observe(s.el);
        });
      }
      state.buildSlides = buildSlides;

      function updateNavVisibility() {
        prevBtn.style.visibility = state.index === 0 ? "hidden" : "visible";
        nextBtn.style.visibility = state.index === state.videos.length - 1 ? "hidden" : "visible";
      }
      state.updateNavVisibility = updateNavVisibility;

      function goTo(idx) {
        if (idx < 0 || idx >= state.slides.length) return;
        state.slides[idx].el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
      }

      overlay.querySelector(".video-reels__overlay-close").addEventListener("click", () => closeOverlay(root));
      prevBtn.addEventListener("click", () => goTo(state.index - 1));
      nextBtn.addEventListener("click", () => goTo(state.index + 1));
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) closeOverlay(root);
      });

      document.addEventListener("keydown", (e) => {
        if (overlay.style.display === "none" || !overlay.style.display) return;
        if (e.key === "Escape") closeOverlay(root);
        if (e.key === "ArrowUp" || e.key === "ArrowLeft") goTo(state.index - 1);
        if (e.key === "ArrowDown" || e.key === "ArrowRight") goTo(state.index + 1);
      });
    }

    state.videos = videos;
    state.index = startIndex;
    state.openTrackedIndex = startIndex;
    sendEvent(apiBase, videos[startIndex].id, "open", placement, store);
    state.buildSlides();
    state.updateNavVisibility();
    state.overlay.style.cssText = overlayOpenStyle;
    document.body.style.overflow = "hidden";
    if (state.slides[startIndex]) {
      state.slides[startIndex].el.scrollIntoView({ behavior: "auto", block: "nearest", inline: "nearest" });
    }
  }

  function closeOverlay(root) {
    const state = rootOverlays.get(root);
    if (!state) return;
    state.overlay.style.cssText = overlayStyle;
    document.body.style.overflow = "";
    state.slides.forEach((s) => s.video.pause());
  }

  function sendEvent(apiBase, videoId, type, placementHandle, store) {
    const payload = JSON.stringify({ videoId, type, placementHandle, store });
    const url = `${apiBase}/api/analytics/event`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true });
    }
  }

  function escapeHtml(str) {
    return String(str || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }
})();
